import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Chip,
  Tooltip,
  Alert,
  Snackbar
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChatIcon from '@mui/icons-material/Chat';
import useOffensiveStore from '../../stores/offensiveStore';
import { createAgentAdapter } from '../../services/agentInterface';
import offensiveApi from '../../services/offensiveApi';
import './EngagementControl.css';

const EngagementControl = ({ onToggleChat, chatOpen, isFlowActive }) => {
  const {
    isRunning,
    startEngagement,
    stopEngagement,
    currentEngagement,
    findings,
    techniques,
    activeFlowId,
    setActiveFlowId,
    reset: resetStore
  } = useOffensiveStore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  const [scanPhase, setScanPhase] = useState(null);
  const [scanMessage, setScanMessage] = useState(null);
  const lastPersistedProgress = useRef(0);

  // Agent adapter — handles mock or real backend based on REACT_APP_AGENT_MODE
  const [agent] = useState(() =>
    createAgentAdapter({
      onEvent: (event) => {
        const store = useOffensiveStore.getState();
        // Route events the store already knows how to handle
        if (['tactic_started', 'tactic_completed', 'technique_queued', 'technique_started',
             'technique_output', 'technique_completed', 'engagement_completed'].includes(event.type)) {
          store.handleWsEvent(event);
        }
        // Handle scan_progress events (UI-local state)
        if (event.type === 'scan_progress') {
          const pct = event.data.percent;
          setScanProgress(pct);
          setScanPhase(event.data.phase);
          setScanMessage(event.data.message);

          // Persist progress to DB on meaningful changes (every 10% or at 100%)
          const engId = store.currentEngagement?.id;
          if (engId && (pct >= 100 || pct - lastPersistedProgress.current >= 10)) {
            lastPersistedProgress.current = pct;
            offensiveApi.updateEngagement(engId, { progress: Math.min(pct, 100) }).catch(() => {});
          }

          if (pct >= 100) {
            setTimeout(() => {
              setScanProgress(null);
              setScanPhase(null);
              setScanMessage(null);
            }, 5000);
          }
        }
      },
      onChatChunk: () => {},
      onError: (msg) => {
        setError(msg);
        setSnackbarOpen(true);
      },
    })
  );

  // Cleanup on unmount only
  useEffect(() => {
    return () => agent.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-reconnect to an active flow on mount (e.g. after page refresh or navigation)
  const reconnected = useRef(false);
  useEffect(() => {
    if (reconnected.current) return;
    if (activeFlowId && !agent._flowId && typeof agent.reconnect === 'function') {
      reconnected.current = true;
      console.log('[PentAGI] Reconnecting to flow', activeFlowId);
      agent.reconnect(activeFlowId);
      startEngagement();
    }
  }, [activeFlowId, agent]);

  const handleStart = async () => {
    if (!currentEngagement?.id) {
      setError('No engagement selected');
      setSnackbarOpen(true);
      return;
    }

    try {
      setLoading(true);
      setScanProgress(null);
      setScanPhase(null);
      setScanMessage(null);

      const result = await agent.start(currentEngagement.id, {
        depth: currentEngagement.scope?.ai_config?.orchestration?.max_depth || 3,
      });
      startEngagement();
      if (result?.executionId) {
        setActiveFlowId(result.executionId);
      }
    } catch (err) {
      setError(err.message);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    if (!currentEngagement?.id) return;
    try {
      setLoading(true);
      await agent.stop(currentEngagement.id);
      agent.disconnect();
      reconnected.current = false;
      resetStore();
      setScanProgress(null);
      setScanPhase(null);
      setScanMessage(null);
    } catch (err) {
      setError(err.message);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (!activeFlowId || !agent) return;
    agent.disconnect();
    reconnected.current = false;
    agent.reconnect(activeFlowId);
  };

  const runningTechniques = techniques.filter(t => t.status === 'running').length;
  const completedTechniques = techniques.filter(t => t.status === 'completed').length;

  // Engagement label
  const engLabel = currentEngagement
    ? `${currentEngagement.code || ''} ${currentEngagement.title || `Engagement #${currentEngagement.id}`}`.trim()
    : null;
  const engCustomer = currentEngagement?.customer?.name;

  return (
    <Box className="engagement-control">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          p: 2,
          background: 'rgba(15, 23, 42, 0.8)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: '1px solid rgba(20, 184, 166, 0.15)',
        }}
      >
        {/* Left Section - Engagement Info + Mode & Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Engagement Indicator */}
          {engLabel && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1, borderRight: '1px solid rgba(255,255,255,0.1)', pr: 2 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: isRunning ? '#2dd4bf' : 'rgba(100,116,139,0.5)', flexShrink: 0, animation: isRunning ? 'pulse-dot 2s infinite' : 'none' }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#e2e8f0', display: 'block', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }} title={engLabel}>
                  {engLabel}
                </Typography>
                {engCustomer && (
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'rgba(148,163,184,0.7)', display: 'block', lineHeight: 1.2 }}>
                    {engCustomer}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Playback Controls */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isRunning ? (
              <Tooltip title="Start Engagement">
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleStart}
                  disabled={!currentEngagement || loading}
                  startIcon={loading ? <RefreshIcon /> : <PlayArrowIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #059669 0%, #059669 100%)',
                    color: '#fff',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #047857 0%, #065f46 100%)',
                    },
                    '&.Mui-disabled': {
                      background: 'rgba(107, 114, 128, 0.3)',
                      color: 'rgba(255, 255, 255, 0.3)',
                    }
                  }}
                >
                  {loading ? 'Starting...' : 'Start'}
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="Stop Engagement">
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleStop}
                  disabled={loading}
                  startIcon={loading ? <RefreshIcon /> : <StopIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
                    color: '#fff',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #991b1b 0%, #b91c1c 100%)',
                    }
                  }}
                >
                  {loading ? 'Stopping...' : 'Stop'}
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Right Section - Stats, Refresh & Interact */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Scan Progress */}
          {scanProgress !== null && (
            <Box sx={{ minWidth: 160, maxWidth: 220 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                  {scanPhase || 'Scanning...'}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600 }}>
                  {scanProgress}%
                </Typography>
              </Box>
              <Box sx={{ width: '100%', height: 6, background: 'rgba(255, 255, 255, 0.1)', borderRadius: 3, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${scanProgress}%`,
                    background: scanPhase === 'error'
                      ? 'linear-gradient(90deg, #b91c1c 0%, #991b1b 100%)'
                      : scanPhase === 'completed'
                      ? 'linear-gradient(90deg, #059669 0%, #059669 100%)'
                      : 'linear-gradient(90deg, #14b8a6 0%, #0d9488 100%)',
                    transition: 'width 0.3s ease',
                    borderRadius: 3,
                  }}
                />
              </Box>
              {scanMessage && (
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.6)', display: 'block', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={scanMessage}>
                  {scanMessage}
                </Typography>
              )}
            </Box>
          )}

          {/* Status Chips */}
          {isRunning && (
            <Chip
              label="Polling"
              size="small"
              sx={{
                height: 24, fontSize: '0.7rem', fontWeight: 600,
                background: 'rgba(20, 184, 166, 0.15)', color: '#14b8a6',
                border: '1px solid rgba(20, 184, 166, 0.3)',
                '& .MuiChip-icon': { width: 8, height: 8, borderRadius: '50%', background: '#14b8a6', animation: 'pulse-dot 2s infinite' }
              }}
              icon={<span />}
            />
          )}
          {runningTechniques > 0 && (
            <Chip label={`${runningTechniques} Running`} size="small" sx={{ height: 24, fontSize: '0.7rem', background: 'rgba(245, 158, 11, 0.2)', color: '#b45309' }} />
          )}
          {completedTechniques > 0 && (
            <Chip label={`${completedTechniques} Completed`} size="small" sx={{ height: 24, fontSize: '0.7rem', background: 'rgba(16, 185, 129, 0.2)', color: '#059669' }} />
          )}
          {findings.length > 0 && (
            <Chip label={`${findings.length} Finding${findings.length !== 1 ? 's' : ''}`} size="small" sx={{ height: 24, fontSize: '0.7rem', fontWeight: 600, background: 'rgba(239, 68, 68, 0.2)', color: '#b91c1c' }} />
          )}

          {/* Refresh */}
          <Tooltip title="Refresh flow data">
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={!activeFlowId}
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                '&:hover': { color: '#2dd4bf', background: 'rgba(20, 184, 166, 0.1)' },
                '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.2)' }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {/* Interact with flow */}
          <Tooltip title={isFlowActive ? (chatOpen ? 'Close chat' : 'Interact with flow') : 'Start a flow to interact'} placement="bottom">
            <span>
              <IconButton
                size="small"
                onClick={onToggleChat}
                disabled={!isFlowActive}
                sx={{
                  color: chatOpen ? '#a5b4fc' : (isFlowActive ? '#818cf8' : 'rgba(148,163,184,0.3)'),
                  background: chatOpen ? 'rgba(99, 102, 241, 0.2)' : 'transparent',
                  border: '1px solid',
                  borderColor: chatOpen ? 'rgba(99, 102, 241, 0.4)' : (isFlowActive ? 'rgba(99, 102, 241, 0.25)' : 'rgba(148,163,184,0.1)'),
                  '&:hover': { background: 'rgba(99, 102, 241, 0.15)', color: '#a5b4fc' },
                  '&.Mui-disabled': { color: 'rgba(148,163,184,0.3)' },
                }}
              >
                <ChatIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EngagementControl;
